import { API_HEADERS, consoleRoutes } from "@sagot/constants";
import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Skeleton, Stepper, toast } from "@sagot/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../../../auth/components";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { MetaPermissionsList, useMessengerSteps } from "../channels/messenger-dialogs";
import { PagePicker } from "./page-picker";
import { clearPendingConnect, readPendingConnect, type PendingMetaConnect } from "./pending-connect";
import { MissingPermissions, RepickHelp, SharedPagesNote, UnavailablePages } from "./repick-help";
import { assignLocation, useRestartConnect, type Redirect } from "./use-restart-connect";

/** Meta (Facebook Login) OAuth redirect target (backend `META_REDIRECT_PATH`, DEC-024). */
export const META_CALLBACK_PATH = "/integrations/meta/callback";

const P = "deployKnowledge.metaCallback";
const MAX_PARAM = 4096;

function backHref(pending: PendingMetaConnect | null): string {
  return pending ? consoleRoutes.channels(pending.wsSlug, pending.agentId) : "/";
}

function BackButton({ pending }: { pending: PendingMetaConnect | null }) {
  const { t } = useTranslation();
  return (
    <Button asChild variant="secondary">
      <AppLink href={backHref(pending)}>
        <Icon name="arrow-left" />
        {t(pending ? `${P}.back` : `${P}.backHome`)}
      </AppLink>
    </Button>
  );
}

function usePagesSelection(pending: PendingMetaConnect, code: string, state: string) {
  const api = useApi();
  return useQuery({
    // The OAuth state is single-use server-side: never refetch or retry this call.
    queryKey: ["meta-callback", state],
    queryFn: () =>
      unwrap(
        api.POST("/v1/agents/{agent_id}/channels/messenger/callback", {
          params: { path: { agent_id: pending.agentId }, header: { [API_HEADERS.workspace]: pending.wsId } },
          body: { code, state },
        }),
      ),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

interface ConnectFlowProps {
  pending: PendingMetaConnect;
  code: string;
  state: string;
  redirect: Redirect;
}

function ConnectFlow({ pending, code, state, redirect }: ConnectFlowProps) {
  const { t } = useTranslation();
  const api = useApi();
  const { restart, busy } = useRestartConnect(pending, redirect);
  const navigate = useNavigate();
  const message = useErrorMessage();
  const steps = useMessengerSteps();
  const selection = usePagesSelection(pending, code, state);
  const [pageId, setPageId] = useState<string | null>(null);
  const select = useMutation({
    mutationFn: (page: string) =>
      unwrap(
        api.POST("/v1/agents/{agent_id}/channels/messenger/pages", {
          params: { path: { agent_id: pending.agentId }, header: { [API_HEADERS.workspace]: pending.wsId } },
          body: { page_id: page, selection_token: selection.data?.selection_token ?? "" },
        }),
      ),
    onSuccess: (_channel, page) => {
      const name = selection.data?.pages.find((p) => p.id === page)?.name ?? "";
      clearPendingConnect();
      toast.success(t(`${P}.connected`, { page: name }));
      void navigate(backHref(pending), { replace: true });
    },
    onError: (error) => toast.error(message(error)),
  });

  if (selection.isPending) {
    return (
      <div className="space-y-3" aria-busy="true">
        <p className="text-sm text-fg-muted">{t(`${P}.loading`)}</p>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (selection.isError) {
    return <ErrorState title={t(`${P}.failedTitle`)} description={message(selection.error) || t(`${P}.failedBody`)} />;
  }
  const { pages, shared_page_count: sharedCount, missing_permissions: missing, unavailable_page_ids: unavailable } = selection.data;
  if (missing.length > 0) return <MissingPermissions scopes={missing} onRestart={restart} busy={busy} back={<BackButton pending={pending} />} />;
  if (pages.length === 0) {
    const actions = (
      <div className="flex flex-wrap justify-center gap-2">
        <BackButton pending={pending} />
        <Button onClick={restart} loading={busy}>
          <Icon name="refresh-cw" />
          {t(`${P}.repickAction`)}
        </Button>
      </div>
    );
    return (
      <div className="space-y-4">
        <EmptyState icon="search" title={t(`${P}.noPagesTitle`)} description={t(`${P}.noPagesBody`)} action={actions} />
        <UnavailablePages ids={unavailable} />
      </div>
    );
  }
  const chosen = pageId ?? pages[0]?.id ?? null;
  return (
    <div className="space-y-5">
      <Stepper steps={steps} current={chosen ? 2 : 1} aria-label={t(`${P}.title`)} />
      <div>
        <h2 className="text-base font-bold">{t(`${P}.pickTitle`)}</h2>
        <p className="mt-1 text-sm text-fg-muted">{t(`${P}.pickBody`)}</p>
      </div>
      <PagePicker pages={pages} value={chosen} onValueChange={setPageId} />
      <SharedPagesNote count={sharedCount} available={pages.length} />
      <UnavailablePages ids={unavailable} />
      <RepickHelp onRestart={restart} busy={busy} />
      <MetaPermissionsList />
      <div className="flex justify-between gap-2">
        <BackButton pending={pending} />
        <Button disabled={!chosen} loading={select.isPending} onClick={() => { if (chosen) select.mutate(chosen); }}>
          <Icon name="plug" />
          {t(`${P}.connect`)}
        </Button>
      </div>
    </div>
  );
}

function readParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);
  return value && value.length <= MAX_PARAM ? value : null;
}

/** `/integrations/meta/callback?code&state` — finish the Messenger connect started on Channels. */
export function MetaCallbackPage({ redirect = assignLocation }: { redirect?: Redirect }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [pending] = useState(() => readPendingConnect());
  const code = readParam(params, "code");
  const state = readParam(params, "state");
  const denied = params.has("error");
  const body = denied ? (
    <EmptyState icon="x-circle" title={t(`${P}.deniedTitle`)} description={t(`${P}.deniedBody`)} action={<BackButton pending={pending} />} />
  ) : !pending || !code || !state ? (
    <EmptyState icon="clock" title={t(`${P}.missingTitle`)} description={t(`${P}.missingBody`)} action={<BackButton pending={pending} />} />
  ) : (
    <ConnectFlow pending={pending} code={code} state={state} redirect={redirect} />
  );
  return (
    <AuthLayout title={t(`${P}.docTitle`)} width="md">
      <div className="space-y-5">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight">
          <Icon name="message-circle" className="size-6 text-primary-ink" />
          {t(`${P}.title`)}
        </h1>
        {body}
      </div>
    </AuthLayout>
  );
}
